import LoggerTypes from '@shared/types/logger';

export const enum SeverityLevel {
    Verbose = 0,
    Information = 1,
    Warning = 2,
    Error = 3,
    Critical = 4,
}

interface CustomDetail {
    properties?: Record<string, unknown>;
    ignoreAdobeAnalytics?: boolean;
}
export interface ErrorDetail extends CustomDetail {
    error: Error;
}
export interface PageViewDetail extends CustomDetail {
    /**
     * name String - The string you used as the name in startTrackPage. Defaults to the document title.
     */
    name?: string;
    /**
     * uri  String - a relative or absolute URL that identifies the page or other item. Defaults to the window location.
     */
    uri?: string;
    /**
     * refUri  String - the URL of the source page where current page is loaded from
     */
    refUri?: string;
    /**
     * pageType  String - page type
     */
    pageType?: string;
    /**
     * isLoggedIn - boolean is user logged in
     */
    isLoggedIn?: boolean;
}
export interface TraceDetail extends CustomDetail {
    message: string;
    severityLevel?: SeverityLevel;
}
export interface EventDetail extends CustomDetail {
    name: string;
}

interface FlagsDetail extends CustomDetail {
    flags: { [key: string]: unknown };
}

function extendDetail<T extends CustomDetail>(detail: T) {
    //NOTE: Will need a polyfill for IE support
    detail.properties
        ? (detail.properties.location = window.location.href)
        : (detail.properties = { location: window.location.href });

    // Stringify number properties
    // https://github.com/microsoft/ApplicationInsights-JS/issues/1778
    if (detail.properties) {
        Object.keys(detail.properties).forEach(key => {
            if (typeof detail.properties[key] == 'number') {
                detail.properties[key] = detail.properties[key].toString();
            }
        });
    }

    return detail;
}

const AppLogger = {
    logException: (detail: ErrorDetail) => {
        detail = extendDetail(detail);
        const customEvent = new CustomEvent(LoggerTypes.EXCEPTION, { detail: detail });
        window.dispatchEvent(customEvent);
    },
    logTrace: (detail: TraceDetail) => {
        detail = extendDetail(detail);
        const customEvent = new CustomEvent(LoggerTypes.TRACE, { detail: detail });
        window.dispatchEvent(customEvent);
    },
    logEvent: (detail: EventDetail) => {
        detail = extendDetail(detail);
        const customEvent = new CustomEvent(LoggerTypes.EVENT, { detail: detail });
        window.dispatchEvent(customEvent);
    },
    logPageView: (detail: PageViewDetail) => {
        detail = extendDetail(detail);
        const customEvent = new CustomEvent(LoggerTypes.PAGE_VIEW, { detail: detail });
        window.dispatchEvent(customEvent);
    },
    setRemoteName: (detail: EventDetail) => {
        detail = extendDetail(detail);
        const customEvent = new CustomEvent(LoggerTypes.REMOTE_LOADED, { detail: detail });
        window.dispatchEvent(customEvent);
    },
    setFeatureFlags: (detail: FlagsDetail) => {
        detail = extendDetail(detail);
        const customEvent = new CustomEvent(LoggerTypes.FEATURE_FLAG, { detail: detail });
        window.dispatchEvent(customEvent);
    },
};
export default AppLogger;
